import {
  ColGroupDef,
  SuppressHeaderKeyboardEventParams,
} from '@ag-grid-community/core';
import {
  AgGridHelperService,
  ProductColGroupId,
} from '@products/fund-listing/services/ag-grid-helper.service';
import { productTypeSingularMapper } from '@products/utils/mappers/product.type.mapper';
import { TranslateService } from '@shared/translate/translate.service';
import { ConfigurationId, ProductType, SuppressDetails } from '@types';
import { FundListingCombinedController } from '../controllers';
import { getPerformanceColumnDefsForUS_VIP } from './col-def-us-vip';

const getHeaderName = (
  listingController: FundListingCombinedController,
  translateService: TranslateService
): string =>
  `${productTypeSingularMapper(
    listingController.productType as ProductType,
    translateService
  )} ${translateService.instant('products.name')}`;

/**
 * Return default combined column definitions for US Digital Servicing.
 * @param agGridHelperService ag grid helper service instance
 */
export function getDefaultCombinedColumnDefsForUS(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  let colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.FUND
        );
      },
      children: [
        agGridHelperService.getFundNameColDef(
          listingController.productType !== ConfigurationId.TIF,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops Sales Charges Toggle overlapping
            minWidth: 190,
          },
          null,
          null,
          null,
          true,
          true,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.MORNINGSTAR,
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getMorningStarOverallRatingColDef(
          listingController,
          {
            autoHeight: true,
            flex: 0,
            minWidth: 230,
          }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.YIELD,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.stdYieldThirtyDayAsofDate,
      },
      children: [
        agGridHelperService.get30DayStandardizedYieldWithoutWaiverColDef(
          listingController,
          {
            minWidth: 120,
            maxWidth: 120,
          }
        ),
        agGridHelperService.get30DayStandardizedYieldWithWaiverColDef(
          listingController,
          {
            minWidth: 130,
            maxWidth: 130,
          }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNAVDistributionRateColDef(listingController, {
          minWidth: 110,
          maxWidth: 110,
        }),
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          width: 80,
          wrapText: false,
          cellStyle: { 'white-space': 'normal' },
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.PERFORMANCE
        );
      },
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ? 90 : 75,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 75,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ? 120 : 115,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ? 90 : 75,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 75,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width:
            listingController.productType === ConfigurationId.TOF ? 90 : 70,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width:
              listingController.productType === ConfigurationId.TOF ? 120 : 115,
          }
        ),
        agGridHelperService.getExpenseRatioColDef(listingController, {
          minWidth: 115,
          maxWidth: 115,
        }),
      ],
    },
  ];

  if (listingController.productType === ConfigurationId.TOF) {
    // Filter out Rating group for TOF
    colGroupDefs = colGroupDefs.filter(
      (group: ColGroupDef): boolean =>
        group.groupId !== ProductColGroupId.MORNINGSTAR
    );
  }

  return colGroupDefs;
}

/**
 * Return CEF combined column definitions for US Digital Servicing.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getCombinedColumnDefsForUS_CEF(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getFundNameColDef(
          false,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops NAV / MARKET PRICE Toggle overlapping
            minWidth: 200,
          },
          null,
          null,
          null,
          true,
          null,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavValueColDef(listingController, {
          width: 107,
        }),
        agGridHelperService.getNavChangeValueColDef(listingController, {
          width: 107,
        }),
        agGridHelperService.getNavYTDReturnColDef(listingController),
        agGridHelperService.getNAVDistributionRateColDef(listingController, {
          minWidth: 110,
          maxWidth: 110,
        }),
        agGridHelperService.getMarketPriceValueColDef(listingController, {
          width: 107,
        }),
        agGridHelperService.getMarketPriceChangeValueColDef(listingController, {
          width: 107,
        }),
        agGridHelperService.getMarketPriceYTDReturnColDef(listingController, {
          minWidth: 110,
          maxWidth: 110,
        }),
        agGridHelperService.getMarketPriceDistributionRateColDef(
          listingController,
          {
            minWidth: 110,
            maxWidth: 110,
          }
        ),
        agGridHelperService.getPremiumDiscountColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getDailyVolumeColDef(listingController, {
          maxWidth: 200,
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 100,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 130,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 100,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 100,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 130,
          }
        ),
      ],
    },
  ];
  return colGroupDefs;
}

/**
 * Return ETF combined column definitions for US Digital Servicing.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getCombinedColumnDefsForUS_ETF(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getFundNameColDef(
          false,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops NAV / MARKET PRICE Toggle overlapping
            minWidth: 200,
          },
          null,
          null,
          null,
          true,
          null,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.YIELD,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.stdYieldThirtyDayAsofDate,
      },
      children: [
        agGridHelperService.get30DayStandardizedYieldWithoutWaiverColDef(
          listingController,
          {
            minWidth: 100,
            maxWidth: 100,
          }
        ),
        agGridHelperService.get30DayStandardizedYieldWithWaiverColDef(
          listingController,
          {
            minWidth: 110,
            maxWidth: 110,
          }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNAVDistributionRateColDef(listingController, {
          minWidth: 110,
          maxWidth: 110,
        }),
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          headerName: translateService.instant(
            'products.ytd-total-returns-nav'
          ),
          width: 80,
        }),
        agGridHelperService.getMarketPriceDistributionRateColDef(
          listingController,
          {
            minWidth: 110,
            maxWidth: 110,
          }
        ),
        agGridHelperService.getMarketPriceYTDReturnColDef(listingController, {
          headerName: translateService.instant(
            'products.ytd-total-returns-market-price'
          ),
          minWidth: 110,
          maxWidth: 110,
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 115,
          },
          false
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 115,
          },
          false
        ),
        agGridHelperService.getAvgAnnualInceptionDateColDef(listingController, {
          width: 115,
        }),
        agGridHelperService.getExpenseRatioColDef(listingController, {
          autoHeight: true,
          minWidth: 115,
          maxWidth: 115,
        }),
      ],
    },
  ];
  return colGroupDefs;
}

/**
 * Return Interval Funds combined column definitions for US Digital Servicing.
 * @param agGridHelperService ag grid helper service instance
 */
export function getCombinedColumnDefsForUS_IF(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.FUND
        );
      },
      children: [
        agGridHelperService.getFundNameColDef(
          true,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops Sales Charges Toggle overlapping
            minWidth: 190,
          },
          null,
          null,
          null,
          true,
          true,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNAVDistributionRateColDef(listingController, {
          minWidth: 110,
          maxWidth: 110,
        }),
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          width: 80,
          wrapText: false,
          cellStyle: { 'white-space': 'normal' },
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.PERFORMANCE
        );
      },
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 75,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 75,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 115,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 75,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 75,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 115,
          }
        ),
        agGridHelperService.getExpenseRatioColDef(listingController, {
          minWidth: 115,
          maxWidth: 115,
        }),
      ],
    },
  ];

  return colGroupDefs;
}
/**
 * Return performance column definitions for US VIP.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getCombinedColumnDefsForUS_VIP(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = getPerformanceColumnDefsForUS_VIP(
    listingController,
    suppressDetails,
    agGridHelperService,
    translateService
  );
  const fundGroup: ColGroupDef = {
    groupId: ProductColGroupId.FUND,
    headerGroupComponent: 'calcTypeGroupHeaderComponent',
    headerGroupComponentParams: listingController,
    children: [
      agGridHelperService.getFundNameColDef(
        false,
        {
          headerName: getHeaderName(listingController, translateService),
          // stops Sales Charges Toggle overlapping
          minWidth: 190,
        },
        null,
        null,
        null,
        true,
        false,
        true
      ),
      agGridHelperService.getShareClassNameColDef(listingController),
    ],
  };
  colGroupDefs[0] = fundGroup;
  return colGroupDefs;
}

/**
 * Return Money Market Funds combined column definitions for US Digital Servicing.
 * @param agGridHelperService ag grid helper service instance
 */
export function getCombinedColumnDefsForUS_MMF(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.FUND
        );
      },
      children: [
        agGridHelperService.getFundNameColDef(
          true,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops Sales Charges Toggle overlapping
            minWidth: 190,
          },
          null,
          null,
          null,
          true,
          true,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.YIELD,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.get7DayCurrentYieldWithWaiverColDef(
          listingController,
          {
            minWidth: 125,
            maxWidth: 125,
          }
        ),
        agGridHelperService.get7DayCurrentYieldWithoutWaiverColDef(
          listingController,
          {
            minWidth: 125,
            maxWidth: 125,
          }
        ),
        agGridHelperService.get7DayEffectiveYieldWithWaiverColDef(
          listingController,
          {
            minWidth: 125,
            maxWidth: 125,
          }
        ),
        agGridHelperService.get7DayEffectiveYieldWithoutWaiverColDef(
          listingController,
          {
            minWidth: 130,
            maxWidth: 130,
          }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          width: 130,
          wrapText: false,
          cellStyle: { 'white-space': 'normal' },
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      suppressHeaderKeyboardEvent: (
        params: SuppressHeaderKeyboardEventParams
      ) => {
        return agGridHelperService.suppressHeaderCellTabbing(
          params,
          ProductColGroupId.PERFORMANCE
        );
      },
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 75,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 75,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 115,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 75,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 75,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 70,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 115,
          }
        ),
        agGridHelperService.getExpenseRatioColDef(listingController, {
          minWidth: 115,
          maxWidth: 115,
        }),
      ],
    },
  ];

  return colGroupDefs;
}

/**
 * Return SMA Funds combined column definitions for US Digital Servicing.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getCombinedColumnDefsForUS_SMA(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getFundNameColDef(
          null,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops Performance Toggle overlapping
            minWidth: 190,
          },
          listingController.productType,
          null,
          true, // hide ticker on excel download,
          true,
          null,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.YIELD,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        footnotePlacement: `${
          listingController.componentName
        }:${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}YieldToWorstGrid`,
        asOfDateText: () => listingController.yieldToWorstAsOfDate,
      },
      children: [
        agGridHelperService.getYieldToWorstWeightedAverageColDef(
          listingController
        ),
      ],
    },
    {
      groupId: ProductColGroupId.ADDITIONAL_PERFORMANCE,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        headerLabelKey: 'products.total-returns',
        footnotePlacement: `${
          listingController.componentName
        }:${agGridHelperService.getConfigurationNameForPlacement(
          listingController.productType
        )}TotalReturnsPPSS`,
        asOfDateText: () => listingController.getPerformanceAsOf(),
      },
      children: [
        agGridHelperService.getCumulativeMonthly3MonthsColDef(
          listingController,
          suppressDetails,
          {
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
          }
        ),
        agGridHelperService.getCumulativeMonthlyYtdColDef(
          listingController,
          suppressDetails,
          {
            headerName: translateService.instant(`products.ytd-total-returns`),
            headerComponentParams: {
              isAlignRight: true,
              footnotePlacement: `${
                listingController.componentName
              }:${agGridHelperService.getConfigurationNameForPlacement(
                listingController.productType
              )}YtdTotalReturnsPPSS`,
            },
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
          }
        ),
        agGridHelperService.getCumulativeQuarterly3MonthsColDef(
          listingController,
          suppressDetails,
          {
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
            width: 90,
          }
        ),
        agGridHelperService.getCumulativeQuarterlyYtdColDef(
          listingController,
          suppressDetails,
          {
            headerName: translateService.instant(`products.ytd-total-returns`),
            headerComponentParams: {
              isAlignRight: true,
              footnotePlacement: `${
                listingController.componentName
              }:${agGridHelperService.getConfigurationNameForPlacement(
                listingController.productType
              )}YtdTotalReturnsPPSS`,
            },
            cellClass: [
              'ag-cell-frk--highlightedSecondary',
              'ag-cell-frk--numeric',
            ],
            width: 90,
          }
        ),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly7YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly7YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
      ],
    },
  ];
  return colGroupDefs;
}

/**
 * Return 529 Portfolios combined column definitions for US Digital Servicing.
 * @param listingController ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getCombinedColumnDefsForUS_529(
  listingController: FundListingCombinedController,
  suppressDetails: SuppressDetails,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: ProductColGroupId.FUND,
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getFundNameColDef(
          false,
          {
            autoHeight: true,
            headerName: getHeaderName(listingController, translateService),
            // stops Sales Charges Toggle overlapping
            minWidth: 190,
          },
          null,
          null,
          null,
          true,
          true,
          true
        ),
      ],
    },
    {
      groupId: ProductColGroupId.NAV,
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => listingController.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavYTDReturnColDef(listingController, {
          headerName: translateService.instant(
            'products.ytd-total-returns-nav'
          ),
          // stop as-of date overlapping
          minWidth: 130,
        }),
      ],
    },
    {
      groupId: ProductColGroupId.PERFORMANCE,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: listingController,
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          listingController,
          suppressDetails
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          listingController,
          suppressDetails,
          {
            width: 90,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef(listingController, {
          width: 90,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          listingController,
          suppressDetails
        ),
      ],
    },
    {
      groupId: ProductColGroupId.EXPENSE_RATIO,
      children: [
        agGridHelperService.getTotalAnnualOperatingExpensesColDef(
          listingController,
          {
            width: 115,
          }
        ),
      ],
    },
  ];
  return colGroupDefs;
}
